import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import PhoneInput from '../components/PhoneInput';
import CountrySelect from '../components/CountrySelect';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STUDY_DOCS = [
  { key: 'passport', label: 'Passport Copy', required: false },
  { key: 'academicCertificates', label: 'Academic Certificates', required: false },
  { key: 'transcripts', label: 'Academic Transcripts', required: false },
  { key: 'cv', label: "CV (Master's applicants only)", required: false },
  { key: 'personalStatement', label: 'Personal Statement', required: false },
  { key: 'referenceLetters', label: 'Reference Letters', required: false },
  { key: 'englishTestResults', label: 'English Test Results', required: false },
  { key: 'photo', label: 'Passport-size Photo', required: false },
];

export default function AssessmentPortalStudy() {
  const [searchParams] = useSearchParams();
  const opportunityId = searchParams.get('opportunityId') || null;
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [personal, setPersonal] = useState({
    fullName: '', email: '', phone: '', whatsapp: '',
    nationality: '', dateOfBirth: '', gender: '', address: '',
    passportNumber: '', passportExpiry: '',
    fatherName: '', fatherPhone: '', motherName: '', motherPhone: '',
  });

  const [studyFields, setStudyFields] = useState({
    targetCountry: '', studyLevel: '', fieldOfStudy: '', intendedIntake: '',
    highestQualification: '', previousInstitution: '', englishProficiency: '',
    englishTestScore: '', fundingSource: '',
  });

  const [files, setFiles] = useState({});

  const handleFileChange = (key, fileObj) => setFiles((prev) => ({ ...prev, [key]: fileObj }));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const formResponses = {
        ...studyFields,
        passportNumber: personal.passportNumber,
        passportExpiry: personal.passportExpiry,
        fatherName: personal.fatherName,
        fatherPhone: personal.fatherPhone,
        motherName: personal.motherName,
        motherPhone: personal.motherPhone,
      };

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ type: 'STUDY', opportunityId, personal, formResponses }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Submission failed');

      const applicationId = json.data.id;

      for (const doc of STUDY_DOCS) {
        const file = files[doc.key];
        if (!file) continue;
        const fd = new FormData();
        fd.append('documentType', doc.label);
        fd.append('file', file);
        await fetch(`${API_BASE}/applications/${applicationId}/documents`, {
          method: 'POST',
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: fd,
        });
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-32 pb-20 px-4">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <span className="material-symbols-outlined text-5xl text-green-600">check_circle</span>
            </div>
            <h2 className="text-3xl font-bold text-[#00255a] mb-4">Application Received!</h2>
            <p className="text-[#64748b] mb-8">
              An agent will contact you shortly to guide you through the next steps of your study application.
            </p>
            <button
              onClick={() => navigate('/')}
              className="text-white px-8 py-4 rounded-xl font-semibold"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              Return to Homepage
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#8fb3ff]/20 text-[#174388] text-sm font-semibold mb-2">
              Study Abroad / Scholarship Application
            </span>
          </div>

          <div className="mb-12">
            <div className="flex justify-between items-center relative">
              <div className="absolute top-1/2 left-0 w-full h-0.5 bg-[#c4c6d1] -translate-y-1/2 z-0" />
              <div
                className="absolute top-1/2 left-0 h-0.5 -translate-y-1/2 z-0 transition-all duration-500"
                style={{ width: `${((step - 1) / 2) * 100}%`, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
              />
              {['Personal Info', 'Program Preferences', 'Documents'].map((label, i) => {
                const n = i + 1;
                const active = n <= step;
                return (
                  <div key={label} className="relative z-10 flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                        active ? 'text-white shadow-md' : 'bg-[#e0e3e5] text-[#44474f] border-2 border-[#c4c6d1]'
                      }`}
                      style={active ? { background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' } : {}}
                    >
                      {n < step ? <span className="material-symbols-outlined text-sm">check</span> : n}
                    </div>
                    <span className={`mt-2 text-sm ${active ? 'text-[#00255a] font-bold' : 'text-[#64748b]'}`}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#e2e8f0] p-8 md:p-12 shadow-[0px_10px_30px_rgba(30,60,114,0.05)] min-h-[500px] flex flex-col">
            {error && <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>}

            {step === 1 && (
              <div>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Personal Information</h2>
                  <p className="text-[#64748b]">Please provide your official contact and family details.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Full Name" value={personal.fullName} onChange={(v) => setPersonal({ ...personal, fullName: v })} />
                  <Field label="Email Address" type="email" value={personal.email} onChange={(v) => setPersonal({ ...personal, email: v })} />
                  <PhoneInput label="Phone Number" value={personal.phone} onChange={(v) => setPersonal({ ...personal, phone: v })} />
                  <PhoneInput label="WhatsApp Number" value={personal.whatsapp} onChange={(v) => setPersonal({ ...personal, whatsapp: v })} />
                  <Field label="Nationality" value={personal.nationality} onChange={(v) => setPersonal({ ...personal, nationality: v })} />
                  <Field label="Date of Birth" type="date" value={personal.dateOfBirth} onChange={(v) => setPersonal({ ...personal, dateOfBirth: v })} />
                  <SelectField label="Gender" value={personal.gender} onChange={(v) => setPersonal({ ...personal, gender: v })} options={['Male', 'Female', 'Prefer not to say']} />
                  <Field label="Passport Number" value={personal.passportNumber} onChange={(v) => setPersonal({ ...personal, passportNumber: v })} />
                  <Field label="Passport Expiry" type="date" value={personal.passportExpiry} onChange={(v) => setPersonal({ ...personal, passportExpiry: v })} />
                  <div className="md:col-span-2">
                    <Field label="Home Address" value={personal.address} onChange={(v) => setPersonal({ ...personal, address: v })} />
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-[#e2e8f0]">
                  <h3 className="text-lg font-bold text-[#0f172a] mb-4">Parent / Guardian Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Father's Name" value={personal.fatherName} onChange={(v) => setPersonal({ ...personal, fatherName: v })} />
                    <PhoneInput label="Father's Phone Number" value={personal.fatherPhone} onChange={(v) => setPersonal({ ...personal, fatherPhone: v })} />
                    <Field label="Mother's Name" value={personal.motherName} onChange={(v) => setPersonal({ ...personal, motherName: v })} />
                    <PhoneInput label="Mother's Phone Number" value={personal.motherPhone} onChange={(v) => setPersonal({ ...personal, motherPhone: v })} />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Program Preferences</h2>
                  <p className="text-[#64748b]">Tell us about the program you're aiming for.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2"><label className="text-sm font-semibold text-[#44474f]">Target Country</label><CountrySelect required value={studyFields.targetCountry} onChange={(v) => setStudyFields({ ...studyFields, targetCountry: v })} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] bg-[#f7f9fb] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all" /></div>
                  <SelectField label="Study Level" value={studyFields.studyLevel} onChange={(v) => setStudyFields({ ...studyFields, studyLevel: v })} options={['Undergraduate', "Master's"]} />
                  <Field label="Field of Study" value={studyFields.fieldOfStudy} onChange={(v) => setStudyFields({ ...studyFields, fieldOfStudy: v })} />
                  <SelectField label="Intended Intake (Optional)" value={studyFields.intendedIntake} onChange={(v) => setStudyFields({ ...studyFields, intendedIntake: v })} options={['Fall', 'Spring']} />
                  <SelectField label="Highest Qualification" value={studyFields.highestQualification} onChange={(v) => setStudyFields({ ...studyFields, highestQualification: v })} options={['O-Level', 'A-Level', "Bachelor's", 'Other']} />
                  <Field label="Previous Institution" value={studyFields.previousInstitution} onChange={(v) => setStudyFields({ ...studyFields, previousInstitution: v })} />
                  <SelectField label="English Proficiency" value={studyFields.englishProficiency} onChange={(v) => setStudyFields({ ...studyFields, englishProficiency: v })} options={['IELTS', 'TOEFL', 'Not yet tested', 'Not required']} />
                  <Field label="English Test Score" value={studyFields.englishTestScore} onChange={(v) => setStudyFields({ ...studyFields, englishTestScore: v })} />
                  <SelectField label="Funding Source" value={studyFields.fundingSource} onChange={(v) => setStudyFields({ ...studyFields, fundingSource: v })} options={['Self-sponsored', 'Sponsor', 'Loan', 'Scholarship-dependent']} />
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Upload Documents</h2>
                  <p className="text-[#64748b]">Max 10MB per file. Accepted formats: PDF, JPG, JPEG, PNG, WEBP. <br></br> <b>Upload available documents. The rest can be uploaded later</b></p>
                </div>
                <div className="space-y-4">
                  {STUDY_DOCS.map((doc) => (
                    <div key={doc.key} className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] bg-[#f7f9fb]">
                      <div>
                        <p className="font-semibold text-[#0f172a]">{doc.label} {doc.required && <span className="text-red-500">*</span>}</p>
                        {files[doc.key] && <p className="text-xs text-[#64748b] mt-1">{files[doc.key].name}</p>}
                      </div>
                      <label className="cursor-pointer px-4 py-2 bg-white border border-[#e2e8f0] rounded-lg text-sm font-semibold text-[#00255a] hover:bg-[#f2f4f6]">
                        {files[doc.key] ? 'Replace' : 'Upload'}
                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => handleFileChange(doc.key, e.target.files[0])} />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 flex justify-between items-center border-t border-[#e2e8f0] pt-8">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)} className="px-8 py-3 rounded-full border-2 border-[#c4c6d1] text-[#44474f] font-semibold hover:border-[#00255a] hover:text-[#00255a] transition-all flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm">arrow_back</span> Back
                </button>
              ) : <div />}
              <button
                onClick={() => (step < 3 ? setStep(step + 1) : handleSubmit())}
                disabled={submitting}
                className="px-10 py-3 rounded-full text-white font-semibold shadow-lg hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
              >
                {step < 3 ? (<>Continue <span className="material-symbols-outlined text-sm">arrow_forward</span></>) : submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-semibold text-[#44474f]">{label}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] bg-[#f7f9fb] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all" />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-semibold text-[#44474f]">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] bg-[#f7f9fb] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all">
        <option value="">Select...</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
